/**
 * DelayEvidenceMeter
 *
 * Median shot delivery delay against the ShotsDelayed threshold. Below minSamples the
 * median is shown but marked as too few samples to act on.
 */
import type { DelayEvidence } from '../../models/reliabilityModels';
import { assessDelay, formatSeconds } from '../../utils/reliabilityPresentation';

interface DelayEvidenceMeterProps {
  readonly delay: DelayEvidence;
}

function toPercent(ratio: number): string {
  return `${Math.min(Math.max(ratio, 0), 1) * 100}%`;
}

function sampleText(delay: DelayEvidence, isConclusive: boolean): string {
  const shots = `${delay.sampleCount} shot${delay.sampleCount === 1 ? '' : 's'}`;
  if (delay.sampleCount === 0) return 'No shots in this window';
  return isConclusive
    ? `from ${shots} (min ${delay.minSamples})`
    : `from ${shots}: too few to act on (min ${delay.minSamples})`;
}

export function DelayEvidenceMeter({ delay }: DelayEvidenceMeterProps): JSX.Element {
  const assessment = assessDelay(delay);
  const fillModifier = !assessment.isConclusive
    ? 'inconclusive'
    : assessment.isOverThreshold
      ? 'over'
      : 'under';

  return (
    <div className="reliability-delay">
      <h4 className="reliability-section-title">
        Median delivery delay · last {assessment.windowMinutes} min
      </h4>
      <p className="reliability-delay__value">
        <span className="reliability-delay__median">
          {delay.medianMs !== undefined ? formatSeconds(delay.medianMs) : '—'}
        </span>
        <span className="reliability-delay__samples">
          {sampleText(delay, assessment.isConclusive)}
        </span>
      </p>
      <div className="reliability-delay__track" aria-hidden="true">
        <div
          className={`reliability-delay__fill reliability-delay__fill--${fillModifier}`}
          style={{ width: toPercent(assessment.fillRatio) }}
        />
        <div
          className="reliability-delay__threshold"
          style={{ left: toPercent(assessment.thresholdRatio) }}
        />
      </div>
      <p className="reliability-delay__threshold-label">
        Threshold {formatSeconds(delay.thresholdMs)}
      </p>
    </div>
  );
}
